import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SLA_TONE = { ok: "bg-emerald-100 text-emerald-700", warning: "bg-amber-100 text-amber-700", breached: "bg-rose-100 text-rose-700" };
function fmtWait(mins) {
  if (mins == null) return "";
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
}

const MGMT_ROLES = new Set(["administrator", "partner", "manager"]);

export default function CommSettings() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const isMgmt = MGMT_ROLES.has(user?.role);
  useEffect(() => { api.get("/communications/settings").then((r) => setData(r.data)).catch(() => setData({ channels: [] })); }, []);
  return (
    <div className="p-6 max-w-[1000px] mx-auto space-y-5" data-testid="comm-settings">
      <div><p className="text-xs text-slate-400 uppercase tracking-wide">Communications</p><h1 className="text-2xl font-bold text-slate-900">Communication Settings</h1><p className="text-sm text-slate-500 mt-0.5">Immediate Response Protocol, channel availability and provider status.</p></div>
      <IrpSettingsCard />
      {isMgmt && <MailboxManagementCard />}
      {isMgmt && <SyncStatusCard />}
      {isMgmt && <BlockedSendersCard />}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Channels</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="channel-list">
          {(data?.channels || []).map((c) => {
            const active = c.status === "active";
            return (
              <div key={c.key} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-start justify-between" data-testid={`channel-${c.key}`}>
                <div>
                  <div className="font-semibold text-slate-800">{c.name}</div>
                  <div className="text-xs text-slate-500 capitalize">{c.type}</div>
                  {c.note && <div className="text-xs text-slate-400 mt-1">{c.note}</div>}
                </div>
                <span className={`text-[11px] px-2 py-1 rounded-full ${active ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{active ? "Active" : "Placeholder"}</span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-slate-400">External providers (Email, SMS, WhatsApp, LINE) are integration-ready placeholders. When unconfigured, messages save as placeholders and fall back to the secure portal.</p>
    </div>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function IrpSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(null);
  const [tz, setTz] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/communications/irp-settings").then((r) => { setCfg(r.data.settings); setOpen(r.data.currently_open); setTz(r.data.open_timezone); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return null;
  const toggleDay = (d) => { const s = new Set(cfg.business_days); s.has(d) ? s.delete(d) : s.add(d); setCfg({ ...cfg, business_days: [...s].sort() }); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/communications/irp-settings", {
        enabled: cfg.enabled, business_days: cfg.business_days, business_start: cfg.business_start,
        business_end: cfg.business_end, human_response_sla_minutes: Number(cfg.human_response_sla_minutes),
        ack_business_hours: cfg.ack_business_hours, ack_after_hours: cfg.ack_after_hours,
      });
      toast.success("IRP settings saved"); load();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const inp = "border border-slate-300 rounded-lg px-3 py-2 text-sm";
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="irp-settings-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icons.Zap className="h-5 w-5 text-orange-500" />
          <div><div className="font-semibold text-slate-800">Immediate Response Protocol (v2.0)</div><div className="text-xs text-slate-500">Auto-acknowledgement, team routing & human-response SLA for new inquiries.</div></div>
        </div>
        <span className={`text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${open ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="irp-open-status">
          <span className={`h-2 w-2 rounded-full ${open ? "bg-emerald-500" : "bg-slate-400"}`} />{open ? `Office Open (${tz})` : "Office Closed"}
        </span>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="irp-enabled" />
        Enable automated immediate responses
      </label>
      <div>
        <div className="text-xs text-slate-500 mb-1.5">Business Days</div>
        <div className="flex gap-1.5 flex-wrap">
          {DAYS.map((d, i) => <button key={d} onClick={() => toggleDay(i)} data-testid={`irp-day-${i}`} className={`text-xs px-2.5 py-1 rounded-lg border ${cfg.business_days.includes(i) ? "bg-blue-50 border-blue-400 text-blue-700" : "border-slate-200 text-slate-500"}`}>{d}</button>)}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <label className="text-xs text-slate-500">Start time<input type="time" className={`${inp} w-full mt-1`} value={cfg.business_start} onChange={(e) => setCfg({ ...cfg, business_start: e.target.value })} data-testid="irp-start" /></label>
        <label className="text-xs text-slate-500">End time<input type="time" className={`${inp} w-full mt-1`} value={cfg.business_end} onChange={(e) => setCfg({ ...cfg, business_end: e.target.value })} data-testid="irp-end" /></label>
        <label className="text-xs text-slate-500">Human response SLA (min)<input type="number" min="5" className={`${inp} w-full mt-1`} value={cfg.human_response_sla_minutes} onChange={(e) => setCfg({ ...cfg, human_response_sla_minutes: e.target.value })} data-testid="irp-sla" /></label>
      </div>
      <div className="text-xs text-slate-400">Time zones (office open if within hours in any): {(cfg.timezones || []).join(" · ")}</div>
      <label className="block text-xs text-slate-500">Business-hours auto-reply<textarea className={`${inp} w-full mt-1`} rows={2} value={cfg.ack_business_hours} onChange={(e) => setCfg({ ...cfg, ack_business_hours: e.target.value })} data-testid="irp-ack-business" /></label>
      <label className="block text-xs text-slate-500">After-hours auto-reply<textarea className={`${inp} w-full mt-1`} rows={2} value={cfg.ack_after_hours} onChange={(e) => setCfg({ ...cfg, ack_after_hours: e.target.value })} data-testid="irp-ack-after" /></label>
      <p className="text-[11px] text-slate-400">Merge fields: <code>{"{{name}}"}</code>, <code>{"{{sla}}"}</code></p>
      <div className="flex justify-end"><button onClick={save} disabled={saving} data-testid="irp-save" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">{saving ? "Saving…" : "Save IRP Settings"}</button></div>
    </div>
  );
}

// ── Mailbox Management Card (Management only) ─────────────────────────────────
const STATUS_STYLE = {
  unconfigured: "bg-amber-100 text-amber-700",
  configured: "bg-blue-100 text-blue-700",
  connected: "bg-emerald-100 text-emerald-700",
  healthy: "bg-emerald-100 text-emerald-700",
  error: "bg-rose-100 text-rose-700",
};
const STATUS_ICON = {
  unconfigured: Icons.AlertCircle,
  configured: Icons.CheckCircle2,
  connected: Icons.CheckCircle2,
  healthy: Icons.CheckCircle2,
  error: Icons.XCircle,
};

function MailboxManagementCard() {
  const [mailboxes, setMailboxes] = useState(null);
  const [checking, setChecking] = useState({});
  const [editing, setEditing] = useState(null);  // mailbox_key being edited
  const [editLabel, setEditLabel] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/titan/mailboxes")
      .then((r) => setMailboxes(r.data.mailboxes))
      .catch(() => setMailboxes([]));

  useEffect(() => { load(); }, []);

  const runHealthCheck = async (key) => {
    setChecking((p) => ({ ...p, [key]: true }));
    try {
      await api.post(`/titan/mailboxes/${key}/health-check`);
      toast.success(`Health check complete for ${key}`);
      load();
    } catch (e) {
      toast.error(`Health check failed: ${e?.response?.data?.detail || "Unknown error"}`);
    } finally {
      setChecking((p) => ({ ...p, [key]: false }));
    }
  };

  const openEdit = (mb) => {
    setEditing(mb.mailbox_key);
    setEditLabel(mb.display_label || "");
    setEditNotes(mb.notes || "");
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/titan/mailboxes/${editing}`, { display_label: editLabel, notes: editNotes });
      toast.success("Mailbox updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (mb) => {
    try {
      await api.patch(`/titan/mailboxes/${mb.mailbox_key}`, { is_active: !mb.is_active });
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  if (mailboxes === null)
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Icons.Mail className="h-5 w-5 text-slate-500" />
          <span className="font-semibold text-slate-800">Titan Mailbox Configuration</span>
        </div>
        <div className="text-sm text-slate-400 flex items-center gap-2">
          <Icons.Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="mailbox-management-card">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Icons.Mail className="h-5 w-5 text-indigo-500" />
          <div>
            <div className="font-semibold text-slate-800">Titan Mailbox Configuration</div>
            <div className="text-xs text-slate-500">Stage 2.5 — real IMAP connectivity health checks. Live polling and SMTP remain disabled.</div>
          </div>
        </div>
        <span className="text-[11px] px-2.5 py-1 rounded-full bg-violet-50 text-violet-600 font-medium">Stage 2.5</span>
      </div>

      <div className="space-y-3" data-testid="mailbox-list">
        {mailboxes.length === 0 && (
          <p className="text-sm text-slate-400">No mailboxes configured. Restart the server to seed canonical mailboxes.</p>
        )}
        {mailboxes.map((mb) => {
          const StatusIcon = STATUS_ICON[mb.connection_status] || Icons.AlertCircle;
          const isChecking = checking[mb.mailbox_key];
          return (
            <div
              key={mb.mailbox_key}
              className={`rounded-lg border p-4 space-y-3 transition-opacity ${mb.is_active ? "border-slate-200" : "border-slate-100 opacity-60"}`}
              data-testid={`mailbox-card-${mb.mailbox_key}`}
            >
              {/* Header row */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                    {mb.display_label}
                    {!mb.is_active && (
                      <span className="text-[10px] bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">Inactive</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5" data-testid={`mailbox-email-${mb.mailbox_key}`}>{mb.email_address}</div>
                  {mb.description && <div className="text-xs text-slate-400 mt-1">{mb.description}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${STATUS_STYLE[mb.connection_status] || "bg-slate-100 text-slate-500"}`}
                    data-testid={`mailbox-status-${mb.mailbox_key}`}
                  >
                    <StatusIcon className="h-3 w-3" />
                    {mb.connection_status}
                  </span>
                </div>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div>
                  <span className="text-slate-400 block">Team routing</span>
                  <span className="text-slate-700 font-medium" data-testid={`mailbox-team-${mb.mailbox_key}`}>
                    {mb.team_name || <span className="text-amber-600">Unassigned (intake)</span>}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block">IMAP host</span>
                  <span className="text-slate-700 font-mono">{mb.imap_host}:{mb.imap_port}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Credential env var</span>
                  <span className="text-slate-600 font-mono text-[10px]">{mb.credential_env_key}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Credential</span>
                  <span
                    className={`font-medium ${mb.credential_present ? "text-emerald-600" : "text-rose-500"}`}
                    data-testid={`mailbox-cred-${mb.mailbox_key}`}
                  >
                    {mb.credential_present ? "Present" : "Not set"}
                  </span>
                </div>
              </div>

              {/* Locks status */}
              <div className="flex gap-2 text-[10px]">
                <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Icons.Lock className="h-2.5 w-2.5" />Live polling: disabled
                </span>
                <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Icons.Lock className="h-2.5 w-2.5" />SMTP: disabled
                </span>
              </div>

              {/* Last health check */}
              {mb.last_health_check_at && (
                <div className="text-[11px] text-slate-400">
                  Last check: {new Date(mb.last_health_check_at).toLocaleString()}
                  {mb.health_check_error && (
                    <span className="ml-2 text-rose-500">{mb.health_check_error}</span>
                  )}
                </div>
              )}

              {/* Notes */}
              {mb.notes && <div className="text-xs text-slate-500 italic">{mb.notes}</div>}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => runHealthCheck(mb.mailbox_key)}
                  disabled={isChecking}
                  data-testid={`mailbox-health-check-${mb.mailbox_key}`}
                  className="text-xs bg-white border border-slate-300 hover:border-indigo-400 hover:text-indigo-600 text-slate-600 rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isChecking ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Activity className="h-3 w-3" />}
                  {isChecking ? "Checking…" : "Run Health Check"}
                </button>
                <button
                  onClick={() => openEdit(mb)}
                  data-testid={`mailbox-edit-${mb.mailbox_key}`}
                  className="text-xs bg-white border border-slate-300 hover:border-slate-400 text-slate-600 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
                >
                  <Icons.Pencil className="h-3 w-3" />Edit
                </button>
                <button
                  onClick={() => toggleActive(mb)}
                  data-testid={`mailbox-toggle-active-${mb.mailbox_key}`}
                  className={`text-xs border rounded-lg px-3 py-1.5 flex items-center gap-1.5 ${mb.is_active ? "bg-white border-slate-300 text-slate-500 hover:border-rose-300 hover:text-rose-500" : "bg-white border-emerald-300 text-emerald-600 hover:bg-emerald-50"}`}
                >
                  {mb.is_active ? <><Icons.EyeOff className="h-3 w-3" />Deactivate</> : <><Icons.Eye className="h-3 w-3" />Activate</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="mailbox-edit-modal">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Edit Mailbox</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600"><Icons.X className="h-4 w-4" /></button>
            </div>
            <label className="block text-xs text-slate-500">
              Display label
              <input
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                data-testid="mailbox-edit-label-input"
              />
            </label>
            <label className="block text-xs text-slate-500">
              Notes (management-visible only)
              <textarea
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                rows={3}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                data-testid="mailbox-edit-notes-input"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-sm text-slate-500 px-3 py-2">Cancel</button>
              <button
                onClick={saveEdit}
                disabled={saving}
                data-testid="mailbox-edit-save"
                className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-3">
        Excluded: <span className="font-mono">mitch.prop@expattaxfirm.com</span> (personal mailbox — excluded from all email integration framework operations).
      </div>
    </div>
  );
}

// ── Sync Status Card (Stage 2.5) ─────────────────────────────────────────────
const SYNC_RESULT_STYLE = {
  success: "bg-emerald-100 text-emerald-700",
  error: "bg-rose-100 text-rose-700",
  partial: "bg-amber-100 text-amber-700",
  locked: "bg-slate-100 text-slate-500",
};

function SyncStatusCard() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState({});
  const [seeding, setSeeding] = useState({});
  const [piloting, setPiloting] = useState({});
  const [activating, setActivating] = useState({});

  const loadStatus = () =>
    api.get("/titan/live-sync/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ mailboxes: [] }));

  useEffect(() => { loadStatus(); }, []);

  const toggleSync = async (key, enabled) => {
    try {
      await api.post(`/titan/live-sync/${key}/${enabled ? "enable" : "disable"}`);
      toast.success(`Sync ${enabled ? "enabled" : "disabled"} for ${key}`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const seedMessages = async (key) => {
    setSeeding((p) => ({ ...p, [key]: true }));
    try {
      const r = await api.post(`/titan/live-sync/${key}/simulate`, null, { params: { reset: true } });
      toast.success(`Seeded ${r.data.seeded} test messages for ${key}`);
      loadStatus();
    } catch (e) {
      toast.error("Seed failed");
    } finally {
      setSeeding((p) => ({ ...p, [key]: false }));
    }
  };

  const runSync = async (key) => {
    setRunning((p) => ({ ...p, [key]: true }));
    try {
      const r = await api.post(`/titan/live-sync/${key}/run`);
      const d = r.data;
      toast.success(`Sync complete: ${d.messages_imported} imported, ${d.messages_deduped} deduped, ${d.messages_blocked} blocked`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setRunning((p) => ({ ...p, [key]: false }));
    }
  };

  const togglePilot = async (key, activate) => {
    setActivating((p) => ({ ...p, [key]: true }));
    try {
      await api.post(`/titan/live-sync/${key}/${activate ? "activate-preview-pilot" : "deactivate-preview-pilot"}`);
      toast.success(`Preview pilot ${activate ? "activated" : "deactivated"} for ${key}`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operation failed");
    } finally {
      setActivating((p) => ({ ...p, [key]: false }));
    }
  };

  const runPilot = async (key) => {
    setPiloting((p) => ({ ...p, [key]: true }));
    try {
      const r = await api.post(`/titan/live-sync/${key}/run-pilot`);
      const d = r.data;
      toast.success(
        `Pilot complete: ${d.messages_found ?? 0} found, ${d.messages_imported ?? 0} imported, ` +
        `${d.messages_deduped ?? 0} deduped, ${d.messages_blocked ?? 0} blocked`
      );
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pilot sync failed");
    } finally {
      setPiloting((p) => ({ ...p, [key]: false }));
    }
  };

  if (!status) return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-2 text-slate-500">
        <Icons.RefreshCw className="h-4 w-4 animate-spin" /><span className="text-sm">Loading sync status…</span>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="sync-status-card">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Icons.RefreshCw className="h-5 w-5 text-violet-500" />
          <div>
            <div className="font-semibold text-slate-800">Inbound Sync — Stage 2.5</div>
            <div className="text-xs text-slate-500">Simulated sync (Stage 2) and real IMAP Preview pilot (Stage 2.5). Activate pilot per mailbox then run a bounded real IMAP fetch.</div>
          </div>
        </div>
        <span className="text-[11px] px-2.5 py-1 rounded-full bg-violet-50 text-violet-600 font-medium">Stage 2.5</span>
      </div>
      <div className="space-y-3" data-testid="sync-mailbox-list">
        {(status.mailboxes || []).map((mb) => {
          const isRunning = running[mb.mailbox_key];
          const isSeeding = seeding[mb.mailbox_key];
          const isPiloting = piloting[mb.mailbox_key];
          const isActivating = activating[mb.mailbox_key];
          const resultStyle = SYNC_RESULT_STYLE[mb.last_sync_result] || "bg-slate-100 text-slate-500";
          const pending = mb.simulated_inbox?.pending_unprocessed || 0;
          return (
            <div key={mb.mailbox_key} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`sync-card-${mb.mailbox_key}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <span className="font-semibold text-slate-800 text-sm">{mb.display_label}</span>
                  <span className="ml-2 text-xs font-mono text-slate-400">{mb.email_address}</span>
                </div>
                <div className="flex items-center gap-2">
                  {mb.sync_in_progress && (
                    <span className="text-[11px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Icons.Loader2 className="h-2.5 w-2.5 animate-spin" />Running
                    </span>
                  )}
                  {mb.preview_pilot_active && (
                    <span className="text-[11px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`pilot-active-badge-${mb.mailbox_key}`}>
                      <Icons.Radio className="h-2.5 w-2.5" />Pilot Active
                    </span>
                  )}
                  {mb.last_sync_result && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${resultStyle}`} data-testid={`sync-result-${mb.mailbox_key}`}>
                      {mb.last_sync_result}
                    </span>
                  )}
                  <button
                    onClick={() => toggleSync(mb.mailbox_key, !mb.sync_enabled)}
                    data-testid={`sync-toggle-${mb.mailbox_key}`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${mb.sync_enabled ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-slate-50 border-slate-300 text-slate-500 hover:border-violet-300 hover:text-violet-600"}`}
                  >
                    {mb.sync_enabled ? "Sync On" : "Sync Off"}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><span className="text-slate-400 block">Messages imported</span><span className="font-semibold text-slate-700" data-testid={`sync-imported-${mb.mailbox_key}`}>{mb.total_messages_imported}</span></div>
                <div><span className="text-slate-400 block">Cycles run</span><span className="font-semibold text-slate-700">{mb.total_sync_cycles}</span></div>
                <div><span className="text-slate-400 block">Pending messages</span><span className={`font-semibold ${pending > 0 ? "text-amber-600" : "text-slate-700"}`}>{pending}</span></div>
              </div>
              {mb.last_sync_at && (
                <div className="text-[11px] text-slate-400">Last sync: {new Date(mb.last_sync_at).toLocaleString()}</div>
              )}
              {mb.last_error && (
                <div className="text-[11px] text-rose-500 truncate" data-testid={`sync-error-${mb.mailbox_key}`}>{mb.last_error}</div>
              )}

              {/* Stage 2: Simulated sync controls */}
              <div className="flex gap-2 flex-wrap pt-1">
                <button
                  onClick={() => seedMessages(mb.mailbox_key)}
                  disabled={isSeeding}
                  data-testid={`seed-messages-${mb.mailbox_key}`}
                  className="text-xs bg-white border border-slate-300 hover:border-violet-400 hover:text-violet-600 text-slate-600 rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSeeding ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Database className="h-3 w-3" />}
                  {isSeeding ? "Seeding…" : "Seed Test Messages"}
                </button>
                <button
                  onClick={() => runSync(mb.mailbox_key)}
                  disabled={isRunning || !mb.sync_enabled}
                  data-testid={`run-sync-${mb.mailbox_key}`}
                  className="text-xs bg-white border border-slate-300 hover:border-violet-400 hover:text-violet-600 text-slate-600 rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                  title={!mb.sync_enabled ? "Enable sync first" : ""}
                >
                  {isRunning ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Play className="h-3 w-3" />}
                  {isRunning ? "Running…" : "Run Sync"}
                </button>
              </div>

              {/* Stage 2.5: Real IMAP Preview Pilot controls */}
              <div className="border-t border-dashed border-slate-100 pt-2 mt-1">
                <div className="text-[10px] text-slate-400 mb-2 font-semibold uppercase tracking-wide">Stage 2.5 — Real IMAP Pilot</div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => togglePilot(mb.mailbox_key, !mb.preview_pilot_active)}
                    disabled={isActivating || (!mb.preview_pilot_active && !mb.credential_present)}
                    data-testid={`pilot-toggle-${mb.mailbox_key}`}
                    title={!mb.credential_present && !mb.preview_pilot_active ? "Credential must be set before activating pilot" : ""}
                    className={`text-xs border rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50 transition-colors ${
                      mb.preview_pilot_active
                        ? "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                        : "bg-white border-slate-300 text-slate-600 hover:border-violet-400 hover:text-violet-600"
                    }`}
                  >
                    {isActivating
                      ? <Icons.Loader2 className="h-3 w-3 animate-spin" />
                      : mb.preview_pilot_active
                        ? <Icons.ZapOff className="h-3 w-3" />
                        : <Icons.Zap className="h-3 w-3" />
                    }
                    {isActivating ? "…" : mb.preview_pilot_active ? "Deactivate Pilot" : "Activate Real IMAP Pilot"}
                  </button>
                  {mb.preview_pilot_active && (
                    <button
                      onClick={() => runPilot(mb.mailbox_key)}
                      disabled={isPiloting}
                      data-testid={`run-pilot-${mb.mailbox_key}`}
                      className="text-xs bg-violet-600 hover:bg-violet-700 text-white border border-violet-600 rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {isPiloting ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Wifi className="h-3 w-3" />}
                      {isPiloting ? "Syncing…" : "Run Real IMAP Pilot (7d, max 25)"}
                    </button>
                  )}
                  {!mb.credential_present && !mb.preview_pilot_active && (
                    <span className="text-[10px] text-amber-600 flex items-center gap-1">
                      <Icons.AlertCircle className="h-3 w-3" />
                      Credential not set — run health check first
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-3">
        Stage 2.5: Health checks pass, then activate pilot per mailbox and run a bounded real IMAP fetch (last 7 days, max 25 messages). Recurring polling disabled. SMTP disabled.
      </div>
    </div>
  );
}

// ── Blocked Senders Card (Stage 2) ────────────────────────────────────────────
function BlockedSendersCard() {
  const [data, setData] = useState(null);
  const [quarantine, setQuarantine] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newReason, setNewReason] = useState("spam");
  const [blocking, setBlocking] = useState(false);
  const [tab, setTab] = useState("blocked"); // "blocked" | "quarantine"

  const loadBlocked = () =>
    api.get("/titan/blocked-senders").then((r) => setData(r.data)).catch(() => setData({ blocked_senders: [] }));
  const loadQuarantine = () =>
    api.get("/titan/blocked-senders/quarantine").then((r) => setQuarantine(r.data)).catch(() => setQuarantine({ quarantined: [] }));

  useEffect(() => { loadBlocked(); loadQuarantine(); }, []);

  const blockSender = async () => {
    if (!newEmail.trim()) return;
    setBlocking(true);
    try {
      const r = await api.post("/titan/blocked-senders", { email: newEmail.trim(), reason: newReason });
      if (r.data.warning) {
        toast.warning(r.data.message);
      } else {
        toast.success(`${newEmail} blocked. ${r.data.conversations_quarantined} conversation(s) quarantined.`);
        setNewEmail("");
        loadBlocked();
        loadQuarantine();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Block failed");
    } finally {
      setBlocking(false);
    }
  };

  const unblock = async (id, email) => {
    try {
      await api.delete(`/titan/blocked-senders/${id}`);
      toast.success(`${email} unblocked`);
      loadBlocked();
    } catch (e) {
      toast.error("Unblock failed");
    }
  };

  const restore = async (convId) => {
    try {
      await api.post(`/titan/blocked-senders/quarantine/${convId}/restore`);
      toast.success("Conversation restored");
      loadQuarantine();
    } catch (e) {
      toast.error("Restore failed");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="blocked-senders-card">
      <div className="flex items-center gap-2">
        <Icons.ShieldOff className="h-5 w-5 text-rose-500" />
        <div>
          <div className="font-semibold text-slate-800">Blocked Senders</div>
          <div className="text-xs text-slate-500">Block exact email addresses across all three operational mailboxes.</div>
        </div>
      </div>

      {/* Add block */}
      <div className="flex gap-2 items-end flex-wrap">
        <label className="flex-1 min-w-[200px]">
          <span className="text-xs text-slate-500 block mb-1">Email address to block</span>
          <input
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="sender@example.com"
            data-testid="block-email-input"
            onKeyDown={(e) => e.key === "Enter" && blockSender()}
          />
        </label>
        <label>
          <span className="text-xs text-slate-500 block mb-1">Reason</span>
          <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm" value={newReason} onChange={(e) => setNewReason(e.target.value)} data-testid="block-reason-select">
            {["spam", "marketing", "harassment", "unwanted", "manual"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <button
          onClick={blockSender}
          disabled={blocking || !newEmail.trim()}
          data-testid="block-sender-button"
          className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
        >
          {blocking ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.ShieldOff className="h-3.5 w-3.5" />}
          Block Sender
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-100">
        {[["blocked", "Blocked Senders", data?.active_count], ["quarantine", "Quarantine", quarantine?.total]].map(([t, label, count]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${tab === t ? "border-rose-500 text-rose-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {label} {count != null && <span className="ml-1 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{count}</span>}
          </button>
        ))}
      </div>

      {/* Blocked list */}
      {tab === "blocked" && (
        <div className="space-y-2" data-testid="blocked-list">
          {!data ? <div className="text-sm text-slate-400">Loading…</div> :
          data.blocked_senders.length === 0 ? <div className="text-sm text-slate-400">No blocked senders.</div> :
          data.blocked_senders.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2.5" data-testid={`blocked-item-${b.id}`}>
              <div>
                <span className="text-sm font-mono text-slate-700" data-testid={`blocked-email-${b.id}`}>{b.email}</span>
                <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 px-1.5 rounded-full">{b.reason}</span>
                {b.associated_client_name && <span className="ml-2 text-[10px] text-amber-600">Client: {b.associated_client_name}</span>}
              </div>
              <button onClick={() => unblock(b.id, b.email)} data-testid={`unblock-${b.id}`}
                className="text-xs text-slate-400 hover:text-rose-500 flex items-center gap-1">
                <Icons.X className="h-3 w-3" />Unblock
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Quarantine list */}
      {tab === "quarantine" && (
        <div className="space-y-2" data-testid="quarantine-list">
          {!quarantine ? <div className="text-sm text-slate-400">Loading…</div> :
          quarantine.quarantined.length === 0 ? <div className="text-sm text-slate-400">Quarantine is empty.</div> :
          quarantine.quarantined.map((q) => (
            <div key={q.id} className="flex items-center justify-between gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2.5" data-testid={`quarantine-item-${q.id}`}>
              <div className="min-w-0">
                <span className="text-xs font-mono text-rose-700 block truncate">{q.contact_email}</span>
                <span className="text-xs text-slate-500 truncate block">{q.subject}</span>
                <span className="text-[10px] text-slate-400">{q.inbound_mailbox_key} · {q.blocked_at ? new Date(q.blocked_at).toLocaleDateString() : ""}</span>
              </div>
              <button onClick={() => restore(q.id)} data-testid={`restore-quarantine-${q.id}`}
                className="text-xs text-slate-500 hover:text-emerald-600 border border-slate-200 hover:border-emerald-300 rounded-lg px-2 py-1 flex items-center gap-1 shrink-0">
                <Icons.RotateCcw className="h-3 w-3" />Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function UnansweredQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/communications/queues/unanswered").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-5" data-testid="unanswered-queue">
      <div><p className="text-xs text-slate-400 uppercase tracking-wide">Operations · Communications</p><h1 className="text-2xl font-bold text-slate-900">Unanswered Messages</h1><p className="text-sm text-slate-500 mt-0.5">Conversations awaiting a staff response.</p></div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="unanswered-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">{["Client", "Subject", "Last Message", "Assigned", "Waiting / SLA", "Priority", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {items === null ? <tr><td colSpan={8} className="px-5 py-12 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></td></tr> :
            items.length === 0 ? <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-400">All messages answered. 🎉</td></tr> :
            items.map((c) => (
              <tr key={c.id} onClick={() => navigate(`/communications/center?conversation=${c.id}`)} data-testid={`unanswered-row-${c.id}`} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                <td className="px-3 py-2.5 font-medium text-slate-800">{c.client_name || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600">{c.subject || "—"}</td>
                <td className="px-3 py-2.5 text-slate-500 max-w-[260px] truncate">{c.last_message}</td>
                <td className="px-3 py-2.5 text-slate-600">{c.assigned_user_name || <span className="text-amber-600">Unassigned</span>}</td>
                <td className="px-3 py-2.5">{c.sla_state && c.sla_state !== "answered" ? <span className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${SLA_TONE[c.sla_state]}`}><Icons.Clock className="h-3 w-3" />{fmtWait(c.waiting_minutes)}</span> : <span className="text-xs text-slate-300">—</span>}</td>
                <td className="px-3 py-2.5 capitalize"><span className={c.priority === "high" ? "text-rose-600 font-semibold" : "text-slate-500"}>{c.priority}</span></td>
                <td className="px-3 py-2.5 capitalize text-slate-500">{(c.conv_status || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2.5 text-right">{c.unread_count > 0 && <span className="text-[11px] bg-rose-500 text-white rounded-full px-2 py-0.5">{c.unread_count}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
